<template>
    <Panel header="Conta" :toggleable="true" class="p-mb-4">
        <div class="p-fluid p-formgrid p-grid">
            <div class="p-field p-col-12 p-md-4">
                <label>Nome <span v-if="modo === 'novo'" class="p-error">*</span></label>
                <InputText v-model="conta.nomeConta" :disabled="modo === 'edicao'" :class="{'p-invalid': enviado && !conta.nomeConta}" />
                <small v-if="enviado && !conta.nomeConta" class="p-error">Campo obrigatório.</small>
            </div>
            <div class="p-field p-col-12 p-md-4">
                <label>E-mail <span v-if="modo === 'novo'" class="p-error">*</span></label>
                <InputText v-model="conta.emailConta" :disabled="modo === 'edicao'" :class="{'p-invalid': enviado && !conta.emailConta}" />
                <small v-if="enviado && !conta.emailConta" class="p-error">Campo obrigatório.</small>
            </div>
            <div class="p-field p-col-12 p-md-4">
                <label>Data do Contrato <span v-if="modo === 'novo'" class="p-error">*</span></label>
                <Calendar v-model="conta.dataContrato" dateFormat="dd/mm/yy" :disabled="modo === 'edicao'" :class="{'p-invalid': enviado && !conta.dataContrato}" />
                <small v-if="enviado && !conta.dataContrato" class="p-error">Campo obrigatório.</small>
            </div>
        </div>
        <div class="p-fluid p-formgrid p-grid p-mt-2">
            <!-- Modo de Operação: novo = dropdown, edicao = input + botão -->
            <div v-if="modo === 'novo'" class="p-field p-col-12 p-md-4">
                <label>Modo de Operação <span class="p-error">*</span></label>
                <Dropdown v-model="conta.modoOperacao" :options="modosOperacao" optionLabel="label" optionValue="value" placeholder="Selecione o modo de operação" :class="{'p-invalid': enviado && !conta.modoOperacao}" appendTo="body" />
                <small v-if="enviado && !conta.modoOperacao" class="p-error">Campo obrigatório.</small>
                <small v-if="conta.modoOperacao === 'OFFLINE'" class="p-d-block p-mt-1">
                    O equipamento possui as regras e decide sozinho se libera ou não. Ele apenas reporta (logs) o que aconteceu depois. Configurações das regras no equipamento.
                </small>
                <small v-if="conta.modoOperacao === 'ONLINE'" class="p-d-block p-mt-1">
                    O equipamento é apenas um leitor. Ele identifica o usuário e envia o ID e eventos para o servidor, e o sistema aplica as regras e responde com eventos e liberação.
                </small>
            </div>
            <div v-if="modo === 'edicao'" class="p-field p-col-12 p-md-4">
                <label>Modo de Operação</label>
                <InputText :value="descricaoModoOperacao(conta.modoOperacao)" disabled />
            </div>
            <div v-if="modo === 'edicao'" class="p-field p-col-0 p-d-flex p-ai-end">
                <Button label="Alterar Modo de Operação" icon="pi pi-pencil" class="p-button-secondary" @click="abrirModalModoOperacao" />
            </div>
        </div>

        <div v-if="modo === 'edicao'" class="p-fluid p-formgrid p-grid p-mt-2">
            <div class="p-field p-col-12 p-md-4">
                <label>Status</label>
                <InputText :value="labelStatus(conta.status)" disabled />
            </div>
            <div class="p-field p-col-0 p-d-flex p-ai-end">
                <Button label="Atualizar Status" icon="pi pi-pencil" class="p-button-secondary" @click="exibirModalStatus = true" />
            </div>
        </div>

        <!-- Modal de atualização de status -->
        <Dialog :visible.sync="exibirModalStatus" header="Atualizar Status da Conta" :modal="true" :style="{width: '380px'}">
            <div class="p-fluid">
                <label>Selecione o novo status</label>
                <Dropdown v-model="novoStatus" :options="opcoesStatus" optionLabel="label" optionValue="valor" placeholder="Selecione..." class="p-mt-2" appendTo="body"/>
            </div>
            <template #footer>
                <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="exibirModalStatus = false" />
                <Button label="Confirmar" icon="pi pi-check" @click="confirmarStatus" :loading="atualizandoStatus" :disabled="!novoStatus" />
            </template>
        </Dialog>

        <!-- Modal de alteração de modo de operação -->
        <Dialog :visible.sync="exibirModalModoOperacao" header="Alterar Modo de Operação" :modal="true" :style="{width: '480px'}">
            <div class="p-fluid">
                <Message severity="warn" :closable="false" class="p-mb-3">
                    <b>Atenção: A alteração do modo de operação exige reconfiguração.</b><br/>
                    Ao mudar o modo de operação, todos os equipamentos conectados precisarão ser reconfigurados manualmente para garantir o funcionamento correto.<br/><br/>
                    Deseja continuar?
                </Message>
                <label>Selecione o novo modo de operação</label>
                <Dropdown v-model="novoModoOperacao" :options="modosOperacao" optionLabel="label" optionValue="value" placeholder="Selecione..." class="p-mt-2" appendTo="body" />
                <small v-if="novoModoOperacao === 'OFFLINE'" class="p-d-block p-mt-1">
                    O equipamento possui as regras e decide sozinho se libera ou não. Ele apenas reporta (logs) o que aconteceu depois. Configurações das regras no equipamento.
                </small>
                <small v-if="novoModoOperacao === 'ONLINE'" class="p-d-block p-mt-1">
                    O equipamento é apenas um leitor. Ele identifica o usuário e envia o ID e eventos para o servidor, e o sistema aplica as regras e responde com eventos e liberação.
                </small>
            </div>
            <template #footer>
                <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="exibirModalModoOperacao = false" />
                <Button label="Confirmar" icon="pi pi-check" @click="confirmarModoOperacao" :loading="atualizandoModoOperacao" :disabled="!novoModoOperacao" />
            </template>
        </Dialog>
    </Panel>
</template>

<script>
import ClienteIKAcessoService from '@/service/ClienteIKAcessoService';
import { exibirMensagem } from '@/global';

export default {
    props: {
        modo: { type: String, default: 'novo' },
        clienteId: { type: [String, Number], default: null },
        initialData: { type: Object, default: null }
    },
    data() {
        return {
            enviado: false,
            enviando: false,
            exibirModalStatus: false,
            atualizandoStatus: false,
            novoStatus: null,
            exibirModalModoOperacao: false,
            atualizandoModoOperacao: false,
            novoModoOperacao: null,
            modosOperacao: [
                { label: 'Modo Offline', value: 'OFFLINE' },
                { label: 'Modo Online', value: 'ONLINE' }
            ],
            opcoesStatus: [
                { label: 'Ativo', valor: 'ATIVO' },
                { label: 'Inativo', valor: 'INATIVO' },
                { label: 'Bloqueado', valor: 'BLOQUEADO' },
                { label: 'Aprovação Pendente', valor: 'APROVACAO_PENDENTE' }
            ],
            conta: {
                nomeConta: null,
                emailConta: null,
                dataContrato: null,
                status: null,
                modoOperacao: null
            }
        };
    },
    watch: {
        initialData(newVal) {
            if (newVal) this.conta = { ...this.conta, ...newVal, dataContrato: this._parsarData(newVal.dataContrato) };
        }
    },
    methods: {
        labelStatus(status) {
            const op = this.opcoesStatus.find(o => o.valor === status);
            return op ? op.label : (status || '');
        },
        descricaoModoOperacao(modo) {
            if (modo === 'OFFLINE') return 'Modo Offline';
            if (modo === 'ONLINE') return 'Modo Online';
            return '';
        },
        validar() {
            this.enviado = true;
            const camposBase = [this.conta.nomeConta, this.conta.emailConta, this.conta.dataContrato]
                .every(v => v != null && v !== '');
            if (this.modo === 'novo') {
                return camposBase && !!this.conta.modoOperacao;
            }
            return camposBase;
        },
        getData() {
            return { ...this.conta, dataContrato: this._formatarDataISO(this.conta.dataContrato) };
        },
        abrirModalModoOperacao() {
            this.novoModoOperacao = null;
            this.exibirModalModoOperacao = true;
        },
        confirmarModoOperacao() {
            if (this.conta.modoOperacao === this.novoModoOperacao) {
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: 'O modo de operação selecionado é o mesmo do atual.', life: 3000 });
                return;
            }
            this.atualizandoModoOperacao = true;
            this.clienteService.atualizarModoOperacaoConta(this.clienteId, this.novoModoOperacao)
                .then(() => {
                    this.conta.modoOperacao = this.novoModoOperacao;
                    this.exibirModalModoOperacao = false;
                    this.novoModoOperacao = null;
                    this.$toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Modo de operação atualizado com sucesso.', life: 3000 });
                })
                .catch(erro => { exibirMensagem(this, erro); })
                .finally(() => { this.atualizandoModoOperacao = false; });
        },
        _parsarData(data) {
            if (!data) return null;
            if (data instanceof Date) return data;
            const [ano, mes, dia] = String(data).substring(0, 10).split('-');
            return new Date(Number(ano), Number(mes) - 1, Number(dia));
        },
        _formatarDataISO(data) {
            if (!data) return null;
            const d = new Date(data);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },
        confirmarStatus() {
            if(this.conta.status == this.novoStatus) {
                this.$toast.add({ severity: 'error', summary: 'Erro', detail: 'O status selecionado é o mesmo do atual.', life: 3000 });
                return;
            }
            this.atualizandoStatus = true;
            this.clienteService.atualizarStatusConta(this.clienteId, this.novoStatus)
                .then(() => {
                    this.conta.status = this.novoStatus;
                    this.exibirModalStatus = false;
                    this.novoStatus = null;
                    this.$toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Status atualizado com sucesso.', life: 3000 });
                })
                .catch(erro => { exibirMensagem(this, erro); })
                .finally(() => { this.atualizandoStatus = false; });
        }
    },
    created() {
        this.clienteService = new ClienteIKAcessoService();
    },
    mounted() {
        if (this.initialData) this.conta = { ...this.conta, ...this.initialData, dataContrato: this._parsarData(this.initialData.dataContrato) };
    }
};
</script>
